import React, { useLayoutEffect, useRef, useState } from 'react';
import { components } from 'react-select';
import { PopoverBody, UncontrolledPopover } from 'reactstrap';

import Avatar from 'src/componentes/avatar';
import guid from 'src/utils/guid';

const badgeStyle = {
  flex: '0 0 auto',
  padding: '2px 6px',
  marginRight: 4,
  borderRadius: 2,
  backgroundColor: '#e6e8ea',
  fontSize: 12,
  lineHeight: '20px',
  cursor: 'help',
};

export const compactMultiStyles = {
  valueContainer: (base) => ({
    ...base,
    minWidth: 0,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    position: 'relative',
  }),
  multiValue: (base) => ({
    ...base,
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 1,
  }),
  multiValueLabel: (base) => ({
    ...base,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
};

const CompactMultiValueContainer = (props) => {
  const selected = props.getValue();
  const { compactMultiShowAvatar = false } = props.selectProps;
  const shouldShowAvatar = (item) =>
    typeof compactMultiShowAvatar === 'function'
      ? compactMultiShowAvatar(item)
      : compactMultiShowAvatar;
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const badgeRef = useRef(null);
  const chipRefs = useRef([]);
  const badgeId = useRef(`compact-multi-${guid()}`);
  const [visibleCount, setVisibleCount] = useState(selected.length);
  const [values, input] = props.children;
  const chips = Array.isArray(values) ? values : [values];

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || selected.length <= 1) return;

    const updateVisibleCount = () => {
      const styles = window.getComputedStyle(container);
      const availableWidth =
        container.clientWidth -
        parseFloat(styles.paddingLeft) -
        parseFloat(styles.paddingRight) -
        12;
      const chipWidths = selected.map(
        (_, index) =>
          chipRefs.current[index]?.getBoundingClientRect().width || 0
      );
      const badgeWidth = badgeRef.current.getBoundingClientRect().width + 4;
      let width = 0;
      let nextVisibleCount = 1;

      for (let index = 0; index < selected.length; index += 1) {
        width += chipWidths[index];
        const hasHiddenItems = index < selected.length - 1;
        if (
          index > 0 &&
          width + (hasHiddenItems ? badgeWidth : 0) > availableWidth
        ) {
          break;
        }
        nextVisibleCount = index + 1;
      }

      setVisibleCount((current) =>
        current === nextVisibleCount ? current : nextVisibleCount
      );
    };

    updateVisibleCount();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateVisibleCount);
      return () => window.removeEventListener('resize', updateVisibleCount);
    }

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(container);
    observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [selected]);

  if (selected.length <= 1) {
    return <components.ValueContainer {...props} />;
  }

  const count = Math.min(visibleCount, selected.length);
  const hiddenItems = selected.slice(count);
  const hiddenLabels = hiddenItems
    .map((item) => props.selectProps.getOptionLabel(item))
    .join(', ');

  return (
    <components.ValueContainer
      {...props}
      innerProps={{ ...props.innerProps, ref: containerRef }}
    >
      {chips.slice(0, count)}
      {count < selected.length && (
        <span
          id={badgeId.current}
          aria-label={hiddenLabels}
          tabIndex={0}
          style={badgeStyle}
        >
          +{selected.length - count}
        </span>
      )}
      {hiddenItems.length > 0 && (
        <UncontrolledPopover
          target={badgeId.current}
          trigger="hover focus"
          placement="auto"
          container="body"
          style={{
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(30, 45, 60, 0.16)',
          }}
        >
          <PopoverBody style={{ padding: 0 }}>
            <div
              style={{
                minWidth: 160,
                maxWidth: 300,
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {hiddenItems.map((item, index) => (
                <div
                  key={props.selectProps.getOptionValue(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom:
                      index < hiddenItems.length - 1
                        ? '1px solid #edf0f3'
                        : 'none',
                  }}
                >
                  {shouldShowAvatar(item) ? (
                    <Avatar
                      id={`${badgeId.current}-avatar-${index}`}
                      foto={item.fotoArquivo}
                      label={props.selectProps.getOptionLabel(item)}
                      showLabel={false}
                      showTooltip={false}
                      size={24}
                      border={0}
                      semMargin
                    />
                  ) : (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        flex: '0 0 auto',
                        borderRadius: '50%',
                        backgroundColor: '#1d7789',
                      }}
                    />
                  )}
                  <span
                    style={{
                      color: '#263746',
                      fontSize: 12,
                      lineHeight: '18px',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {props.selectProps.getOptionLabel(item)}
                  </span>
                </div>
              ))}
            </div>
          </PopoverBody>
        </UncontrolledPopover>
      )}
      {input}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          display: 'flex',
          width: 'max-content',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {chips.map((chip, index) => (
          <span
            key={index}
            ref={(element) => {
              chipRefs.current[index] = element;
            }}
            style={{ display: 'inline-flex' }}
          >
            {chip}
          </span>
        ))}
        <span ref={badgeRef} style={badgeStyle}>
          +{selected.length}
        </span>
      </div>
    </components.ValueContainer>
  );
};

export default CompactMultiValueContainer;
